import { useState, useMemo } from 'react';
import { Select, SelectItem, Button } from '@heroui/react';
import { MapPin, Plus } from 'lucide-react';
import { useKnowledgeItems, useKnowledgePages } from '@/api/knowledge';
import { useDrawingViewports } from '@/api/drawing-viewports';
import { KnowledgeItemType } from '@estimator/shared';
import type { DrawingViewport } from '@estimator/shared';
import ViewportDrawingModal from './ViewportDrawingModal';

interface BanquetteViewportSelectorProps {
  projectId: string;
  viewportId: string | null | undefined;
  onViewportChange: (viewportId: string | null, viewport?: DrawingViewport) => void;
}

export default function BanquetteViewportSelector({
  projectId,
  viewportId,
  onViewportChange,
}: BanquetteViewportSelectorProps) {
  const [selectedDrawingId, setSelectedDrawingId] = useState<string>('');
  const [selectedPageId, setSelectedPageId] = useState<string>('');
  const [isDrawingModalOpen, setIsDrawingModalOpen] = useState(false);

  const { data: knowledgeResp } = useKnowledgeItems(projectId, KnowledgeItemType.DRAWING);
  const drawings = useMemo(() => knowledgeResp?.data ?? [], [knowledgeResp]);

  const { data: pages = [] } = useKnowledgePages(selectedDrawingId);
  const { data: viewports = [] } = useDrawingViewports(projectId);

  // Filter viewports for selected page
  const pageViewports = useMemo(
    () => viewports.filter((v) => v.page_id === selectedPageId),
    [viewports, selectedPageId],
  );

  // All viewports for the whole project (for the "existing viewports" selector)
  const hasViewport = !!viewportId;
  const selectedViewport = viewports.find((v) => v.id === viewportId);

  return (
    <div className="space-y-3">
      <h3 className="text-xs font-semibold flex items-center gap-1.5" style={{ color: '#e67e22' }}>
        <MapPin size={12} /> Drawing Viewport
      </h3>

      {/* Show linked viewport if already set */}
      {hasViewport && selectedViewport && (
        <div className="rounded-lg p-2 flex items-center justify-between" style={{ backgroundColor: 'var(--bg-panel)', border: '1px solid var(--border)' }}>
          <div>
            <div className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>{selectedViewport.name}</div>
            <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
              Type: {selectedViewport.viewport_type}
            </div>
          </div>
          <Button size="sm" variant="light" onPress={() => onViewportChange(null)}>
            Change
          </Button>
        </div>
      )}

      {/* Viewport picker when not set or changing */}
      {!hasViewport && (
        <>
          {/* Quick select from existing viewports */}
          {viewports.length > 0 && (
            <Select
              label="Existing Viewports"
              selectedKeys={[]}
              onSelectionChange={(keys) => {
                const id = Array.from(keys)[0] as string;
                if (id) {
                  const vp = viewports.find((v) => v.id === id);
                  onViewportChange(id, vp);
                }
              }}
              size="sm"
              placeholder="Select an existing viewport..."
              classNames={{ trigger: 'bg-[var(--bg-panel)]' }}
            >
              {viewports.map((v) => (
                <SelectItem key={v.id}>
                  {v.name} ({v.viewport_type})
                </SelectItem>
              ))}
            </Select>
          )}

          <div className="text-[10px] text-center" style={{ color: 'var(--text-muted)' }}>
            {viewports.length > 0 ? '— or create new —' : 'Select a drawing to define a new viewport'}
          </div>

          {/* Step 1: Pick drawing */}
          <Select
            label="Drawing"
            selectedKeys={selectedDrawingId ? [selectedDrawingId] : []}
            onSelectionChange={(keys) => {
              const id = Array.from(keys)[0] as string;
              setSelectedDrawingId(id || '');
              setSelectedPageId('');
            }}
            size="sm"
            placeholder="Select drawing..."
            classNames={{ trigger: 'bg-[var(--bg-panel)]' }}
          >
            {drawings.map((d) => (
              <SelectItem key={d.id}>{d.title || (d as any).original_name || d.id}</SelectItem>
            ))}
          </Select>

          {/* Step 2: Pick page */}
          {selectedDrawingId && pages.length > 0 && (
            <Select
              label="Page"
              selectedKeys={selectedPageId ? [selectedPageId] : []}
              onSelectionChange={(keys) => setSelectedPageId((Array.from(keys)[0] as string) || '')}
              size="sm"
              placeholder="Select page..."
              classNames={{ trigger: 'bg-[var(--bg-panel)]' }}
            >
              {pages.map((p) => (
                <SelectItem key={p.id}>
                  Page {p.page_number}
                </SelectItem>
              ))}
            </Select>
          )}

          {/* Step 3: Create viewport on selected page */}
          {selectedPageId && (
            <div>
              {pageViewports.length > 0 && (
                <div className="mb-2">
                  <div className="text-[10px] mb-1" style={{ color: 'var(--text-muted)' }}>Viewports on this page:</div>
                  {pageViewports.map((v) => (
                    <Button
                      key={v.id}
                      size="sm"
                      variant="flat"
                      className="mr-1 mb-1"
                      onPress={() => onViewportChange(v.id, v)}
                    >
                      {v.name}
                    </Button>
                  ))}
                </div>
              )}
              <Button
                size="sm"
                variant="flat"
                startContent={<Plus size={12} />}
                onPress={() => setIsDrawingModalOpen(true)}
                style={{ color: '#e67e22' }}
              >
                Define New Viewport on This Page
              </Button>

              <ViewportDrawingModal
                isOpen={isDrawingModalOpen}
                onClose={() => setIsDrawingModalOpen(false)}
                pageId={selectedPageId}
                projectId={projectId}
                knowledgeItemId={selectedDrawingId}
                pageNumber={pages.find((p) => p.id === selectedPageId)?.page_number ?? 1}
                onViewportCreated={(vp) => onViewportChange(vp.id, vp)}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
